import { type ChangeEvent, type FC, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { BroadcastStateType } from "@/types/graphql/partner.generated";
import {
  broadcastRates,
  formatPercent,
  formatUsd,
  useAutomationsListQuery,
} from "@/partner/entities/marketing";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { interpolationFormat } from "@/partner/shared/lib";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { BROADCAST_FILTER_STATUSES } from "../model/broadcastLabels";
import { BroadcastStateChip } from "./BroadcastStateChip";
import { BroadcastsListToolbar } from "./BroadcastsListToolbar";

interface BroadcastsListProps {
  onEdit: (id: string) => void;
}

/**
 * Список рассылок: Card + MUI Table, выделение строк, пагинация, как
 * EmailTemplatesList. Строка и название открывают диалог рассылки. На
 * мобиле видны только название и состояние.
 */
export const BroadcastsList: FC<BroadcastsListProps> = ({ onEdit }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search, status },
  } = useFilter();
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  // Статус из адреса проверяется по списку: чужое значение в ?filter[status]
  // уронило бы запрос ошибкой enum.
  const statusFilter = BROADCAST_FILTER_STATUSES.find((s) => s === status);
  const { data, loading } = useAutomationsListQuery({
    variables: {
      status: statusFilter,
      search: search || undefined,
      page,
      rowsPerPage,
    },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.automations.nodes ?? [];
  const count = data?.automations.count ?? 0;
  const selectedIds = nodes.map((n) => n.id).filter((id) => selected[id]);
  const numSelected = selectedIds.length;
  // В архив массово нельзя запланированные и отправляющиеся: см.
  // BroadcastEditor.tsx, тот же запрет на архивацию рассылки в процессе.
  const archivableIds = nodes
    .filter(
      (n) =>
        selected[n.id] &&
        (n.state === BroadcastStateType.DraftType ||
          n.state === BroadcastStateType.SentType)
    )
    .map((n) => n.id);
  const allSelected = numSelected > 0 && numSelected === nodes.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;

  const onSelectAll = (event: ChangeEvent<HTMLInputElement>) =>
    setSelected(
      event.target.checked
        ? Object.fromEntries(nodes.map((item) => [item.id, true]))
        : {}
    );

  const percent = (value: number | null) =>
    value === null
      ? t("broadcasts.stats.noData")
      : formatPercent(value, i18n.language);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return <EmptyListCard title={t("broadcasts.list.empty")} />;
  }

  return (
    <Card elevation={0}>
      {numSelected > 0 && (
        <BroadcastsListToolbar
          indeterminate={indeterminate}
          allSelected={allSelected}
          numSelected={numSelected}
          onSelectAllClick={onSelectAll}
          selectedIds={selectedIds}
          archivableIds={archivableIds}
          onCloseSelected={() => setSelected({})}
        />
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={indeterminate}
                checked={allSelected}
                onChange={onSelectAll}
              />
            </TableCell>
            <TableCell>{t("broadcasts.list.name")}</TableCell>
            <TableCell>{t("broadcasts.list.state")}</TableCell>
            {!isMobile && <TableCell>{t("broadcasts.list.date")}</TableCell>}
            {!isMobile && (
              <TableCell align="right">{t("broadcasts.list.sent")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">{t("broadcasts.list.opens")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">{t("broadcasts.list.clicks")}</TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("broadcasts.list.revenue")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((item) => {
            const rates = broadcastRates(item.stats);
            return (
              <TableRow
                key={item.id}
                hover
                selected={Boolean(selected[item.id])}
                onClick={() => onEdit(item.id)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell
                  padding="checkbox"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    color="default"
                    checked={Boolean(selected[item.id])}
                    onChange={(event) =>
                      setSelected({
                        ...selected,
                        [item.id]: event.target.checked,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <ItemTitleLink
                    to={`${PagePaths.broadcasts}?tab=edit&id=${item.id}`}
                    onOpen={() => onEdit(item.id)}
                    color="text.primary"
                    underline="none"
                  >
                    {item.name}
                  </ItemTitleLink>
                </TableCell>
                <TableCell>
                  <BroadcastStateChip state={item.state} />
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    {interpolationFormat(
                      item.scheduledAt ?? item.startedAt ?? item.createdAt,
                      "short",
                      i18n.language
                    )}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">{item.stats.sent}</TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">{percent(rates.open)}</TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">{percent(rates.click)}</TableCell>
                )}
                {!isMobile && (
                  <TableCell align="right">
                    {/* Итог в списке: первые покупки и продления подписок. */}
                    {formatUsd(
                      item.stats.revenueWithRenewalsUSD,
                      i18n.language
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
